import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Palette, RotateCcw, AlertTriangle, CheckCircle2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  bg: "Hintergrund",
  fg: "Text",
  accent: "Akzent",
  nav_bg: "Navigation",
  footer_bg: "Footer BG",
  footer_fg: "Footer Text",
};

const ORDER = ["bg", "fg", "accent", "nav_bg", "footer_bg", "footer_fg"];

// ── WCAG-Kontrast-Berechnung (relative luminance, AA-Schwelle 4.5) ──────────
const hexToRgb = (hex) => {
  if (!hex) return null;
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};
const relLum = ({ r, g, b }) => {
  const [R, G, B] = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};
const contrastRatio = (hex1, hex2) => {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  if (!c1 || !c2) return null;
  const l1 = relLum(c1);
  const l2 = relLum(c2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

// Auto-Brand: dominante Farben aus Logo-URL via Canvas extrahieren
const extractPaletteFromImage = (imgUrl) =>
  new Promise((resolve, reject) => {
    if (!imgUrl) return reject(new Error("kein Logo"));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        const W = 50, H = 50;
        c.width = W; c.height = H;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, W, H);
        const { data } = ctx.getImageData(0, 0, W, H);
        const buckets = {};
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 200) continue;
          // 4-Bit-Quantisieren pro Kanal → 16x16x16 = 4096 Buckets
          const r = data[i] >> 4, g = data[i + 1] >> 4, b = data[i + 2] >> 4;
          const key = (r << 8) | (g << 4) | b;
          buckets[key] = (buckets[key] || 0) + 1;
        }
        const sorted = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
        // Top-3 Buckets in Hex umwandeln und nahezu-weiße/-schwarze überspringen
        const hexes = [];
        for (const [k] of sorted) {
          const r = ((k >> 8) & 0xf) * 17;
          const g = ((k >> 4) & 0xf) * 17;
          const b = (k & 0xf) * 17;
          // skip wenn fast weiß / fast schwarz
          if (r > 235 && g > 235 && b > 235) continue;
          if (r < 25 && g < 25 && b < 25) continue;
          const hex = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
          if (!hexes.includes(hex)) hexes.push(hex);
          if (hexes.length >= 3) break;
        }
        resolve(hexes);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
    img.src = imgUrl;
  });

/**
 * Props:
 *   templateId: string
 *   theme: object (current editContent.theme)
 *   setTheme: (newTheme) => void
 *   presetKey: string (current editContent.theme_preset)
 *   setPresetKey: (key) => void
 *   logoUrl: string (für Auto-Brand-Extraction)
 *   projectId: string (für Backend-Brand-Extraction)
 */
export const ThemeEditor = ({ templateId, theme, setTheme, presetKey, setPresetKey, heroVariant, setHeroVariant, sectionNumber, logoUrl, projectId }) => {
  const [presets, setPresets] = useState([]);
  const [extractingBrand, setExtractingBrand] = useState(false);
  const [brandSuggestion, setBrandSuggestion] = useState(null);

  useEffect(() => {
    if (!templateId) return;
    axios.get(`${API}/templates/${templateId}/theme-presets`, { withCredentials: true })
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => setPresets([]));
  }, [templateId]);

  const applyPreset = (preset) => {
    const { key, label, ...colors } = preset; // eslint-disable-line no-unused-vars
    setTheme(colors);
    setPresetKey(preset.key);
  };

  const reset = () => {
    setTheme({});
    setPresetKey("");
  };

  const setField = (field, value) => {
    setTheme({ ...(theme || {}), [field]: value });
    setPresetKey("custom");
  };

  const currentValue = (field) => (theme && theme[field]) || "";
  const isHex = (v) => /^#[0-9A-Fa-f]{6}$/.test(v);

  // ── Live-Preview-Werte: Falle auf Sinnvolles zurück, wenn Felder leer ──
  const previewBg = currentValue("bg") || "#FAFAFA";
  const previewFg = currentValue("fg") || "#181818";
  const previewAccent = currentValue("accent") || "#0047FF";

  // WCAG-Kontrast für Body-Text und Akzent-Button
  const bodyContrast = useMemo(() => contrastRatio(previewBg, previewFg), [previewBg, previewFg]);
  const accentContrast = useMemo(() => {
    // Akzent-Button hat weiße Schrift in den meisten Templates → gegen weiß checken
    return contrastRatio(previewAccent, "#ffffff");
  }, [previewAccent]);

  const ContrastBadge = ({ label, ratio }) => {
    if (!ratio) return null;
    const pass = ratio >= 4.5;
    const partial = ratio >= 3 && ratio < 4.5;
    const tone = pass ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
      : partial ? "bg-amber-500/10 text-amber-700 border-amber-500/30"
      : "bg-rose-500/10 text-rose-700 border-rose-500/30";
    const Icon = pass ? CheckCircle2 : AlertTriangle;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${tone}`} data-testid={`contrast-${label.toLowerCase()}`}>
        <Icon size={10} />
        {label}: {ratio.toFixed(1)}{pass ? " · AA ✓" : partial ? " · grenzwertig" : " · zu niedrig"}
      </span>
    );
  };

  const handleExtractBrand = async () => {
    if (!logoUrl) {
      setBrandSuggestion({ error: "Lade zuerst ein Logo hoch — dann ziehen wir die Marken-Farben automatisch heraus." });
      return;
    }
    setExtractingBrand(true);
    setBrandSuggestion(null);
    try {
      // Bevorzugt: Backend-basiert (CORS-frei, robust). Fällt nur auf
      // Browser-Canvas zurück, wenn keine projectId verfügbar ist.
      if (projectId) {
        const r = await axios.post(
          `${API}/projects/${projectId}/extract-brand-palette`,
          null,
          { withCredentials: true }
        );
        const palette = r.data?.palette || [];
        if (palette.length === 0) {
          setBrandSuggestion({ error: "Keine eindeutigen Marken-Farben im Logo gefunden." });
        } else {
          setBrandSuggestion({ palette });
        }
      } else {
        const palette = await extractPaletteFromImage(logoUrl);
        setBrandSuggestion(palette.length ? { palette } : { error: "Keine Farben erkannt." });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Logo konnte nicht analysiert werden.";
      setBrandSuggestion({ error: msg });
    } finally {
      setExtractingBrand(false);
    }
  };

  const applyBrandPalette = (palette) => {
    // Strategie: dominanteste Farbe → Akzent. Zweite (kontrastreich) → Footer-BG.
    const accent = palette[0];
    const next = { ...(theme || {}), accent };
    if (palette[1]) next.footer_bg = palette[1];
    setTheme(next);
    setPresetKey("custom");
    setBrandSuggestion(null);
  };

  return (
    <section className="space-y-3" data-testid="theme-editor">
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          {sectionNumber ? (
            <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{sectionNumber}</span>
          ) : (
            <span className="w-6 h-6 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
              <Palette size={12} />
            </span>
          )}
          <h3 className="text-sm font-bold uppercase tracking-widest">Farben & Stil</h3>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={reset} className="h-7 px-2 text-xs rounded-sm gap-1" data-testid="theme-reset-btn">
          <RotateCcw size={11} /> Zurücksetzen
        </Button>
      </div>

      {/* ───── LIVE PREVIEW BAR ─────────────────────────────────────────────── */}
      <div
        className="rounded-md overflow-hidden border border-border shadow-sm transition-all"
        data-testid="theme-live-preview"
        style={{ background: previewBg, color: previewFg }}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3 border-b" style={{ borderColor: `${previewFg}1a` }}>
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ opacity: 0.55 }}>Vorschau</span>
          <div className="flex items-center gap-1.5">
            <ContrastBadge label="Text" ratio={bodyContrast} />
            <ContrastBadge label="Button" ratio={accentContrast} />
          </div>
        </div>
        <div className="px-4 py-4">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: previewAccent }}>— Beispiel-Hero</div>
          <div className="text-base font-bold leading-tight mb-1.5">Headline in deinen Farben</div>
          <div className="text-xs opacity-75 mb-2.5">Beschreibung mit Body-Text — so erscheinen Absätze auf der Demo.</div>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded-sm"
              style={{ background: previewAccent, color: "#fff" }}
            >
              Button
            </span>
            <span className="text-[11px] underline" style={{ opacity: 0.7 }}>Sekundär-Link</span>
          </div>
        </div>
      </div>

      {/* PRESETS */}
      {presets.length > 0 && (
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Vorgefertigte Themes</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {presets.map((p) => {
              const active = presetKey === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`group flex items-center gap-2 p-2 rounded-sm border transition-all text-left ${active ? "border-primary ring-2 ring-primary/40 bg-primary/5 shadow-sm" : "border-border hover:border-foreground/40"}`}
                  data-testid={`theme-preset-${p.key}`}
                >
                  <div className="flex -space-x-1 shrink-0">
                    <div className="w-5 h-5 rounded-full border border-white/30 shadow-sm" style={{ background: p.bg }} />
                    <div className="w-5 h-5 rounded-full border border-white/30 shadow-sm" style={{ background: p.accent }} />
                    <div className="w-5 h-5 rounded-full border border-white/30 shadow-sm" style={{ background: p.footer_bg }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate flex items-center gap-1">
                      {p.label}
                      {active && <CheckCircle2 size={11} className="text-primary shrink-0" />}
                    </div>
                    {active && <div className="text-[10px] text-primary font-bold uppercase tracking-wide">aktiv</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ───── AUTO-BRAND-EXTRACTION ────────────────────────────────────────── */}
      <div className="rounded-sm border border-dashed border-violet-500/40 bg-violet-500/5 p-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5 text-violet-700">
              <Wand2 size={12} /> Marken-Farben aus Logo
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {logoUrl ? "Wir extrahieren die dominantesten Farben aus dem Logo und schlagen sie als Akzent vor." : "Lade zuerst ein Logo hoch — dann erkennen wir die Marken-Farben automatisch."}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleExtractBrand}
            disabled={!logoUrl || extractingBrand}
            className="h-7 px-2.5 text-xs rounded-sm shrink-0"
            data-testid="extract-brand-btn"
          >
            {extractingBrand ? "analysiert..." : "Erkennen"}
          </Button>
        </div>
        {brandSuggestion?.error && (
          <p className="text-[11px] text-rose-700 mt-2" data-testid="brand-error">{brandSuggestion.error}</p>
        )}
        {brandSuggestion?.palette && (
          <div className="mt-2 flex items-center gap-2" data-testid="brand-suggestion">
            <div className="flex gap-1">
              {brandSuggestion.palette.map((hex) => (
                <div key={hex} className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ background: hex }} title={hex} />
              ))}
            </div>
            <Button type="button" size="sm" onClick={() => applyBrandPalette(brandSuggestion.palette)} className="h-7 px-3 text-xs rounded-sm" data-testid="apply-brand-btn">
              Übernehmen
            </Button>
            <button type="button" onClick={() => setBrandSuggestion(null)} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="brand-cancel">
              Verwerfen
            </button>
          </div>
        )}
      </div>

      {/* CUSTOM COLOR PICKERS */}
      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Eigene Farben (überschreibt Preset)</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {ORDER.map((field) => {
            const val = currentValue(field);
            const showVal = isHex(val) ? val : "#000000";
            return (
              <div key={field} className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={showVal}
                  onChange={(e) => setField(field, e.target.value.toUpperCase())}
                  className="w-9 h-8 rounded-sm border border-border cursor-pointer bg-transparent"
                  data-testid={`theme-color-${field}`}
                />
                <div className="flex-1 min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground block leading-tight">{FIELD_LABELS[field]}</Label>
                  <Input
                    value={val}
                    onChange={(e) => setField(field, e.target.value)}
                    placeholder={field === "nav_bg" || field === "footer_fg" ? "rgba(…)" : "#XXXXXX"}
                    className="rounded-sm text-xs h-6 px-1.5 mt-0.5"
                    data-testid={`theme-field-${field}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-muted-foreground italic">Tipp: Wähle ein Preset als Startpunkt und feintune dann einzelne Farben. „Zurücksetzen" nimmt die Template-Standards.</p>

      {/* HERO-LAYOUT-VARIANTE */}
      {typeof setHeroVariant === "function" && (
        <div className="pt-3 border-t border-border">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Hero-Layout (Anordnung)</Label>
          <p className="text-[11px] text-muted-foreground mb-2 italic">Bestimmt nur die Anordnung — Bild/Video/Animation steuerst du in „Hauptbereich".</p>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { key: "default", label: "Zentriert" },
              { key: "split", label: "Split 50/50" },
              { key: "minimal", label: "Minimal" },
            ].map((v) => {
              const active = (heroVariant || "default") === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setHeroVariant(v.key)}
                  className={`text-xs py-1.5 rounded-sm border transition-all flex items-center justify-center gap-1 ${active ? "border-primary bg-primary/5 text-foreground font-semibold ring-2 ring-primary/30" : "border-border text-muted-foreground hover:border-foreground/40"}`}
                  data-testid={`hero-variant-${v.key}`}
                >
                  {active && <CheckCircle2 size={11} className="text-primary" />}
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};
